import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// Async thunks
export const fetchRoutines = createAsyncThunk(
  'routines/fetchRoutines',
  async ({ token, trainerId, search = '', page = 0, size = 10 }) => {
    const searchParam = search ? `&name=${encodeURIComponent(search)}` : ''
    const response = await apiCallWithHost(
      `/programs?trainerId=${trainerId}${searchParam}&page=${page}&size=${size}`,
      {},
      token,
    )
    if (!response.ok) {
      throw new Error('Failed to fetch routines')
    }
    return response.json()
  },
)

export const fetchRoutine = createAsyncThunk(
  'programs/fetchRoutine',
  async ({ token, routineId }) => {
    const response = await apiCallWithHost(`/programs/${routineId}`, {}, token)
    if (!response.ok) {
      throw new Error('Failed to fetch routine')
    }
    return response.json()
  },
)

export const createRoutine = createAsyncThunk(
  'routines/createRoutine',
  async ({ token, routineData }) => {
    const response = await apiCallWithHost(
      `/programs`,
      {
        method: 'POST',
        body: JSON.stringify(routineData),
      },
      token,
    )
    if (!response.ok) {
      throw new Error('Failed to create routine')
    }
    return response.json()
  },
)

export const updateRoutine = createAsyncThunk(
  'routines/updateRoutine',
  async ({ token, routineId, routineData }) => {
    const response = await apiCallWithHost(
      `/programs/${routineId}`,
      {
        method: 'PUT',
        body: JSON.stringify(routineData),
      },
      token,
    )
    if (!response.ok) {
      throw new Error('Failed to update routine')
    }
    return response.json()
  },
)

export const deleteRoutine = createAsyncThunk(
  'routines/deleteRoutine',
  async ({ token, routineId }) => {
    const response = await apiCallWithHost(
      `/programs/${routineId}`,
      {
        method: 'DELETE',
      },
      token,
    )
    if (!response.ok) {
      throw new Error('Failed to delete routine')
    }
    return routineId
  },
)

const initialState = {
  routines: [],
  currentRoutine: null,
  pagination: {
    pageNumber: 0,
    pageSize: 10,
    totalPages: 0,
    totalElements: 0,
  },
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
}

const programsSlice = createSlice({
  name: 'programs',
  initialState,
  reducers: {
    clearRoutines: (state) => {
      state.routines = []
      state.pagination = initialState.pagination
      state.status = 'idle'
      state.error = null
    },
    clearCurrentRoutine: (state) => {
      state.currentRoutine = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch routines
      .addCase(fetchRoutines.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchRoutines.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.routines = action.payload.content
        state.pagination = {
          pageNumber: action.payload.number,
          pageSize: action.payload.size,
          totalPages: action.payload.totalPages,
          totalElements: action.payload.totalElements,
        }
      })
      .addCase(fetchRoutines.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      // Fetch single routine
      .addCase(fetchRoutine.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchRoutine.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.currentRoutine = action.payload
      })
      .addCase(fetchRoutine.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      // Create routine
      .addCase(createRoutine.fulfilled, (state, action) => {
        state.routines.push(action.payload)
        state.pagination.totalElements += 1
      })
      // Update routine
      .addCase(updateRoutine.fulfilled, (state, action) => {
        const index = state.routines.findIndex((p) => p.id === action.payload.id)
        if (index !== -1) {
          state.routines[index] = action.payload
        }
      })
      // Delete routine
      .addCase(deleteRoutine.fulfilled, (state, action) => {
        state.routines = state.routines.filter((p) => p.id !== action.payload)
        state.pagination.totalElements -= 1
      })
  },
})

export const { clearRoutines, clearCurrentRoutine } = programsSlice.actions

// Selectors
export const selectAllRoutines = (state) => state.programs.routines
export const selectCurrentRoutine = (state) => state.programs.currentRoutine
export const selectRoutinesPagination = (state) => state.programs.pagination
export const selectRoutinesStatus = (state) => state.programs.status
export const selectRoutinesError = (state) => state.programs.error

export default programsSlice.reducer
